import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { X } from 'lucide-react';

// ══════════════════════════════════════════════════════════════
// STUDENT TIMELINE MODAL - unified read-only view of all 4
// placement stages: Initial HR Status, Job Offers, Placement
// Status, After Armstech Placement. Used from Placement.js and
// Students.js.
// ══════════════════════════════════════════════════════════════
export default function StudentTimelineModal({ student, onClose }) {
  const [loading, setLoading] = useState(true);
  const [hrStatus, setHrStatus] = useState(null);
  const [offers, setOffers] = useState([]);
  const [placement, setPlacement] = useState(null);
  const [afterRecords, setAfterRecords] = useState([]);

  useEffect(() => {
    if (!student?.id) return;
    setLoading(true);
    Promise.all([
      supabase.from('hr_status').select('*').eq('student_id', student.id).maybeSingle(),
      supabase.from('placement_offers').select('*').eq('student_id', student.id).order('offer_date', { ascending: false }),
      supabase.from('placements').select('*').eq('student_id', student.id).maybeSingle(),
      supabase.from('after_placement').select('*').eq('student_id', student.id).order('created_at', { ascending: false }),
    ]).then(([hr, off, pl, after]) => {
      setHrStatus(hr.data || null);
      setOffers(off.data || []);
      setPlacement(pl.data || null);
      setAfterRecords(after.data || []);
      setLoading(false);
    });
  }, [student?.id]);

  const Row = ({ l, v }) => v ? <div className="detail-row"><div className="detail-label">{l}</div><div className="detail-value">{v}</div></div> : null;
  const Section = ({ title, children, empty }) => (
    <div style={{ marginBottom: 18 }}>
      <div className="form-section-heading" style={{ marginBottom: 8 }}>{title}</div>
      {empty ? <div style={{ fontSize: 12, color: 'var(--text3)', padding: '4px 0' }}>No record yet</div> : children}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">
            {student.full_name}
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text3)', marginLeft: 8 }}>{student.register_number}</span>
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" /></div> : (
            <>
              {/* 1. Initial HR Status */}
              <Section title="① Initial HR Status" empty={!hrStatus}>
                <Row l="Resume Submitted" v={hrStatus?.resume_submitted} />
                <Row l="Field of Interest" v={hrStatus?.field_of_interest} />
                <Row l="English Score" v={hrStatus?.english_score} />
                <Row l="Hindi Score" v={hrStatus?.hindi_score} />
                <Row l="Backpaper Status" v={hrStatus?.backpaper_status} />
                <Row l="Backpaper Clear Time" v={hrStatus?.backpaper_clear_time} />
                <Row l="Previous Experience" v={hrStatus?.previous_experience} />
                <Row l="Current Status" v={hrStatus?.current_status} />
                <Row l="Reason" v={hrStatus?.reason} />
                <Row l="Latest Comment" v={hrStatus?.latest_comment} />
                {(hrStatus?.contacts || []).filter(ct => ct.contacted_date || ct.response).map((ct, i) => (
                  <Row key={i} l={`Contact ${i + 1}`} v={`${ct.contacted_date || ''} — ${ct.response || ''}`} />
                ))}
              </Section>

              {/* 2. Job Offers */}
              <Section title="② Job Offers" empty={offers.length === 0}>
                {offers.map((o, i) => (
                  <div key={o.id} style={{ background: 'var(--bg3)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Offer {i + 1} — {o.company_name || '—'}</div>
                    <Row l="Job Role" v={o.job_role} />
                    <Row l="Location" v={o.job_location} />
                    <Row l="Offered Salary" v={o.offered_salary} />
                    <Row l="Offer Date" v={o.offer_date} />
                    <Row l="Student Status" v={o.student_status} />
                    <Row l="Client Status" v={o.client_status} />
                    <Row l="Rejection Reason" v={o.rejection_reason} />
                    <Row l="Remarks" v={o.remarks} />
                  </div>
                ))}
              </Section>

              {/* 3. Placement Status */}
              <Section title="③ Placement Status" empty={!placement}>
                <Row l="Company" v={placement?.company_name} />
                <Row l="Job Role" v={placement?.job_role} />
                <Row l="Job Location" v={placement?.job_location} />
                <Row l="Working Area" v={placement?.working_area} />
                <Row l="Offered Salary" v={placement?.offered_salary} />
                <Row l="Joining Date" v={placement?.joining_date} />
                <Row l="Sector" v={placement?.sector} />
                <Row l="Project Name" v={placement?.project_name} />
                <Row l="Benefits" v={placement?.benefits} />
                <Row l="Resigned" v={placement?.resigned ? 'Yes' : null} />
                <Row l="Resignation Date" v={placement?.resignation_date} />
                <Row l="Resignation Reason" v={placement?.resignation_reason} />
                <Row l="Future Job Needed" v={placement?.future_job_needed ? 'Yes' : null} />
                {(placement?.followups || []).filter(f => f.date || f.notes).map((f, i) => (
                  <Row key={i} l={`Follow-up ${i + 1}`} v={`${f.date || ''} — ${f.notes || ''}`} />
                ))}
              </Section>

              {/* 4. After Armstech Placement */}
              <Section title="④ After Armstech Placement" empty={afterRecords.length === 0}>
                {afterRecords.map((r, i) => (
                  <div key={r.id} style={{ background: 'var(--bg3)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Record {i + 1}</div>
                    <Row l="Current Contact" v={r.current_contact} />
                    <Row l="Current Company" v={r.current_company} />
                    <Row l="Job Role" v={r.job_role} />
                    <Row l="Working Area" v={r.working_area} />
                    <Row l="Client" v={r.client_name} />
                    <Row l="Project Details" v={r.project_details} />
                    <Row l="Job Location" v={r.job_location} />
                    <Row l="Experience (Years)" v={r.experience_years} />
                    <Row l="Current Salary" v={r.current_salary} />
                    <Row l="Additional Qualification" v={r.additional_qualification} />
                    <Row l="Placement Assistance Needed" v={r.placement_assistance} />
                    <Row l="Armstech Action" v={r.armstech_action} />
                    <Row l="Feedback" v={r.feedback} />
                  </div>
                ))}
              </Section>
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-primary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
